import { Box, Chip, CircularProgress, Divider, Grid2, Stack } from "@mui/material";
import { DesignBoardTypeSelection } from "../design-board-type-selection";
import { DesignBoardForm } from "../design-board-form";
import {
  DesignBoardType,
  DesignBoard,
  PopulatedDesignBoard,
  MediaFile,
} from "../../../../__generated__/types";
import { useFormState } from "../../../../hooks/use-form-state";
import { useUserContext } from "../../../../hooks/userContext";
import { useCreateMoodBoardMutation } from "../../../../hooks/use-create-mood-board";
import { useUpdateMoodBoardMutation } from "../../../../hooks/use-update-mood-board-mutation";
import { useState, useEffect, useCallback, useRef } from "react";
import type { MutableRefObject } from "react";
import { useNavigate } from "react-router";
import { Pages } from "../../../../utils";
import { useNotification } from "../../../../contexts/NotificationContext";
import { LocalProjectFileImage } from "../../add-project/add-project-container";
import { useFirebaseImageUpload } from "../../../../components/firebase-image-upload/use-firebase-image-upload";
import { FirebasePaths } from "../../../../utils/firebase";
import { UploadProjectImage } from "../../add-project/upload-project-image";
import { ProjectImageList } from "../../add-project/project-image-list";
import { useDeleteFirebaseImage } from "../../../../components/firebase-image-upload/use-delete-firebase-image";
import { H6Typography, Body2Typography } from "../../../../components/typography";
import { DesignBoardTypeLabels } from "../../../../utils";
import { ImageDetailsDialog } from "../../../../components/image-details-dialog";
import { useUpdateMediaFileMutation } from "../../../../hooks/use-update-media-file-mutation";
import { designBoardInputRules } from "../utils";

const initialState = {
  name: "",
  description: "",
  why: "",
  color: [],
  texture: [],
  pattern: [],
  finish: [],
  greatFor: "",
  images: [],
  type: "",
  roomType: [],
} as unknown as DesignBoard;

const MAX_DESIGN_BOARD_IMAGES = 15;

const gridColSize = {
  xs: 12,
  md: 6,
};

export const DesignBoardContainer = ({
  moodBoard,
  handleSubmitRef,
  handlePublishRef,
  onPublishStateChange,
  onSetFormValid,
}: {
  moodBoard?: PopulatedDesignBoard | null;
  handleSubmitRef?: MutableRefObject<(() => void) | null>;
  handlePublishRef?: MutableRefObject<((nextPublished: boolean) => void) | null>;
  onPublishStateChange?: (published: boolean) => void;
  onSetFormValid?: (isValid: boolean) => void;
}) => {
  const { loading, onUpload } = useFirebaseImageUpload(
    FirebasePaths.DESIGN_BOARD
  );
  const { user } = useUserContext();
  const { createMoodBoard, loading: createMoodBoardLoading } =
    useCreateMoodBoardMutation();
  const { updateMoodBoard, loading: updateMoodBoardLoading } =
    useUpdateMoodBoardMutation();
  const { deleteFirebaseImage, loading: deleteImageLoading } =
    useDeleteFirebaseImage();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const isEditMode = !!moodBoard;
  const publishOverrideRef = useRef<boolean | null>(null);
  const [isPublished, setIsPublished] = useState<boolean>(() => {
    if (!moodBoard) return false;
    return moodBoard.published ?? true;
  });

  const initialLocalImages = (moodBoard?.images as MediaFile[])?.slice(0, MAX_DESIGN_BOARD_IMAGES).map(
    (image) => ({
      _id: image._id as string,
      local_url: image.url as string,
      title: image.title || undefined,
      credit: (image as any)?.credit || undefined,
      description: image.description || undefined,
    })
  );
  const [localImages, setLocalImages] = useState<LocalProjectFileImage[]>(
    initialLocalImages || []
  );

  // Image details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
  const { updateMediaFile } = useUpdateMediaFileMutation();

  const handleAddImages = useCallback(
    (newImages: { file: File; local_url: string }[]) => {
      setLocalImages((current) => {
        const updated = [...current, ...newImages].slice(0, MAX_DESIGN_BOARD_IMAGES);
        return updated;
      });
    },
    []
  );

  const handleDeleteImage = useCallback(
    async (index: number) => {
      const imageToDelete = localImages[index];
      if (imageToDelete?._id) {
        // Delete from Firebase if it has an _id
        try {
          await deleteFirebaseImage(
            imageToDelete.local_url,
            imageToDelete._id
          );
        } catch (error) {
          console.error("Error deleting image:", error);
        }
      }
      setLocalImages((current) => current.filter((_, i) => i !== index));
    },
    [localImages, deleteFirebaseImage]
  );

  const handleSetCoverImage = useCallback((index: number) => {
    setLocalImages((current) => {
      const updated = [...current];
      const selected = updated[index];
      updated.splice(index, 1);
      updated.unshift(selected);
      return updated;
    });
  }, []);

  const handleEditImageDetails = useCallback((index: number) => {
    setEditingImageIndex(index);
    setDetailsDialogOpen(true);
  }, []);

  const handleSaveImageDetails = useCallback(async (title: string, credit: string, description: string) => {
    if (editingImageIndex === null) return;

    const image = localImages[editingImageIndex];
    if (!image) return;

    // Update local state
    setLocalImages((current) => {
      const updated = [...current];
      updated[editingImageIndex] = {
        ...updated[editingImageIndex],
        title: title || undefined,
        credit: credit || undefined,
        description: description || undefined,
      };
      return updated;
    });

    // If image has an _id (already uploaded), update it in the backend
    if (image._id) {
      try {
        await updateMediaFile({
          _id: image._id,
          title: title || null,
          credit: credit || null,
          description: description || null,
        });
      } catch (error) {
        showError("Failed to update image details. Please try again.");
      }
    }
  }, [editingImageIndex, localImages, updateMediaFile, showError]);

  const getValidationErrors = useCallback((currentState: DesignBoard) => {
    const errs: Partial<Record<keyof DesignBoard, string>> = {};
    const requiredFields = ["name", "description", "why", "greatFor"] as const;

    requiredFields.forEach((field) => {
      const value = (currentState as any)[field];
      const text =
        typeof value === "string"
          ? value.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim()
          : value;
      if (!text || (typeof text === "string" && text.length === 0)) {
        errs[field] = "Required";
      }
    });

    if (!currentState.type) {
      errs.type = "Select a design board type";
    }

    const typeRules = currentState.type
      ? designBoardInputRules[currentState.type as DesignBoardType] || []
      : [];

    typeRules.forEach((rule) => {
      const value = (currentState as any)[rule];
      if (!value || (Array.isArray(value) && value.length === 0)) {
        errs[rule as keyof DesignBoard] = "Required";
      }
    });

    if (!localImages.length) {
      errs.images = "Add at least one image";
    }

    return errs;
  }, [localImages]);

  const sanitizedMoodBoard: Partial<DesignBoard> = moodBoard
    ? {
        _id: moodBoard._id,
        name: moodBoard.name || "",
        description: (moodBoard.description as string) || "",
        why: (moodBoard.why as string) || "",
        color: (moodBoard.color as any) || [],
        texture: (moodBoard.texture as any) || [],
        pattern: (moodBoard.pattern as any) || [],
        finish: (moodBoard.finish as any) || [],
        greatFor: (moodBoard.greatFor as any) || "",
        images:
          (moodBoard.images as MediaFile[])?.map((img) => img._id as string) || [],
        type: (moodBoard.type as any) || "",
        roomType: (moodBoard.roomType as any) || [],
      }
    : {};

  const { state, handleChange, errors, handleSubmit } = useFormState({
    initialState: {
      ...initialState,
      ...sanitizedMoodBoard,
    },
    validate: getValidationErrors,
    onSubmit: async (state) => {
      if (!localImages.length) {
        showError("Please add at least one image.");
        return;
      }

      const newImages = localImages.filter((img) => !!!img?._id);

      // Create a FileList from the new images
      const filesToUpload: File[] = [];
      for (const image of newImages) {
        if (image.file) {
          const fileObj = image.file as any;
          if (fileObj instanceof File) {
            filesToUpload.push(fileObj);
          } else if (fileObj instanceof Blob) {
            const fileName = `image-${Date.now()}.${fileObj.type.includes('png') ? 'png' : 'jpg'}`;
            const fileType = fileObj.type || 'image/jpeg';
            const newFile = new File([fileObj], fileName, { type: fileType });
            filesToUpload.push(newFile);
          }
        }
      }

      // Convert File[] to FileList using DataTransfer
      const dataTransfer = new DataTransfer();
      filesToUpload.forEach((file) => {
        try {
          dataTransfer.items.add(file);
        } catch (error) {
          console.error('Failed to add file to DataTransfer:', error, file);
        }
      });

      const fileList = dataTransfer.files;

      const uploadedImages =
        fileList.length > 0
          ? await onUpload(fileList)
          : [];

      // Get existing image IDs
      const existingImageIds = localImages
        .filter((img) => !!img?._id)
        .map((img) => img._id as string);

      // Get new image IDs from upload and update with title/description/credit
      const newImageIds: string[] = [];
      const updatePromises: Promise<void>[] = [];
      
      if (Array.isArray(uploadedImages) && uploadedImages.length > 0) {
        newImages.forEach((image, index) => {
          const uploaded = uploadedImages[index];
          if (uploaded?._id) {
            newImageIds.push(uploaded._id);
            
            // Update MediaFile with title/description/credit if they exist
            if (image.title || image.description || image.credit) {
              updatePromises.push(
                updateMediaFile({
                  _id: uploaded._id as string,
                  title: image.title || null,
                  credit: image.credit || null,
                  description: image.description || null,
                }).then(() => {
                }).catch((error) => {
                  console.error("Error updating image details after upload:", error);
                })
              );
            }
          }
        });
      }
      
      // Wait for all updates to complete (but don't block on errors)
      if (updatePromises.length > 0) {
        await Promise.allSettled(updatePromises);
      }

      // Combine existing and new image IDs
      const allImageIds = [...existingImageIds, ...newImageIds];

      // Delete images that were removed
      if (moodBoard?.images) {
        const imagesToDelete = (moodBoard.images as MediaFile[]).filter(
          (img) => !allImageIds.includes(img._id as string)
        );
        for (const img of imagesToDelete) {
          try {
            await deleteFirebaseImage(img.url as string, img._id as string);
          } catch (error) {
            console.error("Error deleting removed image:", error);
          }
        }
      }

      const nextPublished = publishOverrideRef.current ?? isPublished;
      const update = {
        ...state,
        designerId: user?.id as string,
        images: allImageIds.length > 0 ? allImageIds : null,
        published: nextPublished,
      };
      try {
        if (!isEditMode) {
          await createMoodBoard(update);
          showSuccess("Design board added successfully.");
        } else {
          await updateMoodBoard({
            ...update,
            _id: moodBoard?._id as string,
          });
          showSuccess("Design board updated successfully.");
        }
        if (nextPublished !== isPublished) {
          setIsPublished(nextPublished);
        }
        onPublishStateChange?.(nextPublished);
        navigate(Pages.MY_DESIGN_FILES, { replace: true });
      } catch (error) {
        showError("Unable to save design board. Please try again.");
        throw error;
      } finally {
        publishOverrideRef.current = null;
      }
    },
  });

  const isLoading = createMoodBoardLoading || updateMoodBoardLoading || loading || deleteImageLoading;

  useEffect(() => {
    if (handleSubmitRef) {
      handleSubmitRef.current = handleSubmit;
    }
    if (handlePublishRef) {
      handlePublishRef.current = async (nextPublished: boolean) => {
        publishOverrideRef.current = nextPublished;
        const didSubmit = await handleSubmit();
        if (!didSubmit) {
          publishOverrideRef.current = null;
        }
      };
    }
  }, [handleSubmit, handleSubmitRef, handlePublishRef]);

  useEffect(() => {
    if (moodBoard) {
      const nextPublished = moodBoard.published ?? true;
      setIsPublished(nextPublished);
    }
  }, [moodBoard]);

  useEffect(() => {
    onPublishStateChange?.(isPublished);
  }, [isPublished, onPublishStateChange]);

  useEffect(() => {
    const validationErrors = getValidationErrors(state as DesignBoard);
    const formValid = !isLoading && Object.keys(validationErrors).length === 0;
    if (onSetFormValid) {
      onSetFormValid(formValid);
    }
  }, [state, isLoading, onSetFormValid, getValidationErrors]);

  return (
    <>
      {isLoading && (
        <Box>
          <CircularProgress sx={{ color: '#00A06D' }} />
        </Box>
      )}

      {!isLoading && (
        <Box>
          <Box
            className="content-block"
            sx={{
              p: "32px",
              mb: "32px",
              borderTopLeftRadius: "0px",
              borderTopRightRadius: "0px",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
              <Chip
                label={isPublished ? "Published" : "Draft"}
                size="small"
                sx={{
                  backgroundColor: isPublished ? "#2E7D32" : "#ED6C02",
                  color: "#fff",
                  fontWeight: 600,
                }}
              />
              <Body2Typography sx={{ color: "text.secondary" }}>
                {isPublished
                  ? "This design board is visible in the Designer Files gallery."
                  : "Publish when you're ready for this design board to appear in the gallery."}
              </Body2Typography>
            </Stack>
            <DesignBoardTypeSelection
              onSelect={handleChange("type")}
              value={state.type as DesignBoardType}
              lockedType={isEditMode ? (moodBoard?.type as DesignBoardType) : undefined}
            />

            {state.type && (
              <>
                <Divider sx={{ mt: "40px" }} />
                <Box mt="48px">
                  <H6Typography sx={{ mb: 3 }}>
                    {DesignBoardTypeLabels[state.type as DesignBoardType]}
                  </H6Typography>
                  <Grid2 container spacing={3}>
                    <Grid2 size={gridColSize}>
                      <Body2Typography mb={2} fontWeight={600} sx={{ fontSize: "1rem" }}>
                        Project Details
                      </Body2Typography>
                      <DesignBoardForm
                        state={state as DesignBoard}
                        handleChange={handleChange}
                        errors={errors}
                      />
                    </Grid2>
                    <Grid2 size={gridColSize}>
                      <Body2Typography mb={2} fontWeight={600} sx={{ fontSize: "1rem" }}>
                        Design Board
                      </Body2Typography>
                      <Box
                        sx={{
                          p: 2,
                          background: "#FBFBFB",
                          border: "1px dashed #D9D9D9",
                          borderRadius: "12px",
                          width: "100%",
                        }}
                      >
                        <UploadProjectImage
                          multiple
                          currentCount={localImages.length}
                          maxCount={MAX_DESIGN_BOARD_IMAGES}
                          onComplete={handleAddImages}
                          label={`Add ${DesignBoardTypeLabels[state.type as DesignBoardType]} Images`}
                          sx={{ width: "100%" }}
                        />

                        {localImages.length > 0 && (
                          <>
                            <Box sx={{ mt: 3, mb: 2 }}>
                              <Body2Typography mb="8px" fontWeight={600}>
                                Design Board Photos
                              </Body2Typography>
                              <Body2Typography sx={{ color: "text.secondary" }}>
                                Upload up to {MAX_DESIGN_BOARD_IMAGES} images. Drag to reorder on desktop
                                or assign numbers on mobile. The first image will be used as the cover photo.
                              </Body2Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                              <ProjectImageList
                                images={localImages}
                                onReorder={setLocalImages}
                                onDelete={handleDeleteImage}
                                onSetCover={handleSetCoverImage}
                                onEditDetails={handleEditImageDetails}
                              />
                            </Box>
                          </>
                        )}
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Image Details Dialog */}
      {editingImageIndex !== null && localImages[editingImageIndex] && (
        <ImageDetailsDialog
          open={detailsDialogOpen}
          onClose={() => {
            setDetailsDialogOpen(false);
            setEditingImageIndex(null);
          }}
          onSave={handleSaveImageDetails}
          imageUrl={localImages[editingImageIndex].local_url}
          currentTitle={localImages[editingImageIndex].title || ""}
          currentCredit={localImages[editingImageIndex].credit || ""}
          currentDescription={localImages[editingImageIndex].description || ""}
        />
      )}
    </>
  );
};
